import { refreshAccessToken } from '@tshared/apis/auth.api'
import { jwtDecode } from 'jwt-decode'

import type { User } from '../models'
import { setUser, useUserStore } from '../stores/user-session.store'

const TOKEN_REFRESH_SKEW_SECONDS = 15 * 60

function shouldRefreshToken(accessToken?: string): boolean {
  if (!accessToken) return true

  try {
    const payload = jwtDecode<{ exp?: number }>(accessToken)
    return !payload.exp || payload.exp < Math.floor(Date.now() / 1_000) + TOKEN_REFRESH_SKEW_SECONDS
  } catch {
    return true
  }
}

export async function refreshUser(force?: boolean): Promise<User | null> {
  const user = useUserStore.getState().user
  if (!user?.refreshToken) {
    if (force) {
      await setUser(null)
      const error = new Error('refreshToken is not set')
      useUserStore.getState().onRefreshTokenError?.(error)
      throw error
    }
    return null
  }

  if (!force && !shouldRefreshToken(user.accessToken)) return user

  try {
    const nextUser = await refreshAccessToken(user.refreshToken)
    const mergedUser = {
      ...user,
      userName: nextUser.userName ?? user.userName,
      accessToken: nextUser.accessToken,
      refreshToken: nextUser.refreshToken,
      expiresTime: nextUser.expiresTime,
    }
    await setUser(mergedUser)
    return mergedUser
  } catch (error) {
    await setUser(null)
    useUserStore.getState().onRefreshTokenError?.(error)
    throw error
  }
}
